import axios, { AxiosInstance, AxiosError } from 'axios';
import { API_BASE_URL } from '../config/api';
import { parseError } from './errorHandler';
import { retryNetworkRequest } from './retryService';

class ApiService {
  private client: AxiosInstance;
  private refreshTokenPromise: Promise<string> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.client.interceptors.request.use(
      async (config: any) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: any) => Promise.reject(error),
    );

    this.client.interceptors.response.use(
      (response: any) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const newToken = await this.refreshAccessToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            await this.logout();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  private async refreshAccessToken(): Promise<string> {
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    this.refreshTokenPromise = (async () => {
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const { access_token } = response.data;
        localStorage.setItem('access_token', access_token);
        return access_token;
      } finally {
        this.refreshTokenPromise = null;
      }
    })();

    return this.refreshTokenPromise;
  }

  private async logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  async get<T>(url: string, config?: any): Promise<T> {
    try {
      const response = await this.client.get<T>(url, config);
      return response.data;
    } catch (error: any) {
      const appError = parseError(error);
      throw appError;
    }
  }

  async post<T>(url: string, data?: any, config?: any): Promise<T> {
    const requestConfig = {
      ...config,
      timeout: config?.timeout || (url.includes('/rides') ? 90000 : 30000),
      cancelToken: config?.cancelToken || undefined,
    };

    const executeRequest = async (): Promise<T> => {
      try {
        const response = await this.client.post<T>(url, data, requestConfig);
        return response.data;
      } catch (error: any) {
        const appError = parseError(error);
        throw appError;
      }
    };

    if (config?.retry !== false) {
      return retryNetworkRequest(executeRequest, {
        maxRetries: config?.maxRetries || 2,
        retryDelay: config?.retryDelay || 1000,
      });
    }

    return executeRequest();
  }

  async put<T>(url: string, data?: any, config?: any): Promise<T> {
    try {
      const response = await this.client.put<T>(url, data, config);
      return response.data;
    } catch (error: any) {
      const appError = parseError(error);
      throw appError;
    }
  }

  async patch<T>(url: string, data?: any, config?: any): Promise<T> {
    try {
      const response = await this.client.patch<T>(url, data, config);
      return response.data;
    } catch (error: any) {
      const appError = parseError(error);
      throw appError;
    }
  }

  async delete<T>(url: string, config?: any): Promise<T> {
    try {
      const response = await this.client.delete<T>(url, config);
      return response.data;
    } catch (error: any) {
      const appError = parseError(error);
      throw appError;
    }
  }

  async upload<T>(url: string, formData: FormData, config?: any): Promise<T> {
    const token = localStorage.getItem('access_token');

    const headers: any = {
      ...config?.headers,
      Authorization: `Bearer ${token}`,
    };


    if (!(formData instanceof FormData)) {
      headers['Content-Type'] = 'multipart/form-data';
    }

    const response = await axios.post<T>(`${API_BASE_URL}${url}`, formData, {
      ...config,
      headers,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });
    return response.data;
  }
}

export const apiService = new ApiService();

